import React, { useState } from 'react';
import { addLateFee, resolveLateFee, outstandingTotal } from '../../../shared/utils/lateFees';

// Admin per-member late-fee panel (mirrors the contribution review, but for fees
// and entirely separate from it). Per fee by state:
//   outstanding — Mark Paid / Waive
//   pending     — Approve Fee Payment / Reject (+reason → Confirm Rejection)
//   resolved    — shown in history only
// Plus Add Late Fee. History shows the full event ledger. Rendered only when the
// group has fees enabled. Writes go through the RPCs; pot/summary cards untouched.
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const shortDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const EVENT_LABEL = { submitted: 'Member submitted', paid: 'Paid', waived: 'Waived', rejected: 'Rejected' };

// `canAdd` gates the Add Late Fee button: the parent passes true only when a
// cycle is running, this member owes on the live round, and the round is past
// due. Absent/false → the button is shown greyed and disabled (fails closed).
// Everything else on the panel (history, Mark Paid / Waive / Approve / Reject)
// stays available whenever fees are enabled.
const MemberLateFees = ({ member, group, fees, canAdd, onChanged }) => {
  const memberFees = fees || [];
  const outstanding = outstandingTotal(memberFees);
  const actionable = memberFees.filter(f => f.state !== 'resolved'); // outstanding or pending

  const defaultAmount = group?.late_fee_amount != null ? String(group.late_fee_amount) : '';
  const [showAdd, setShowAdd] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [amount, setAmount] = useState(defaultAmount);
  const [note, setNote] = useState('');
  const [rejectingId, setRejectingId] = useState(null);   // fee id being rejected
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleAdd = async () => {
    const v = parseFloat(amount);
    if (isNaN(v) || v <= 0) { setError('Amount must be greater than zero.'); return; }
    setBusy(true); setError(null);
    try {
      await addLateFee({ groupId: group.id, memberDbId: member.id, amount: v, note });
      setShowAdd(false); setNote(''); setAmount(defaultAmount);
      onChanged();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const handleResolve = async (feeId, resolution, resNote) => {
    setBusy(true); setError(null);
    try {
      await resolveLateFee({ feeId, resolution, note: resNote });
      setRejectingId(null); setRejectReason('');
      onChanged();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-neutral-100">
      <div className="flex items-center justify-between">
        <span className="text-sm text-neutral-700">
          Late fees outstanding: <span className={`font-semibold ${outstanding > 0 ? 'text-error-600' : 'text-neutral-500'}`}>{fmt(outstanding)}</span>
        </span>
        <div className="flex gap-2">
          {memberFees.length > 0 && (
            <button onClick={() => setShowHistory(v => !v)}
              className="text-xs px-2 py-1 border border-neutral-300 rounded text-neutral-600 hover:bg-neutral-50">
              {showHistory ? 'Hide' : 'History'}
            </button>
          )}
          <button
            onClick={() => { setError(null); setAmount(defaultAmount); setShowAdd(true); }}
            disabled={!canAdd}
            title={canAdd
              ? 'Charge a late fee for this round'
              : 'Available once the cycle is running, this member owes on the current round, and the round is past due'}
            className={`text-xs px-2 py-1 rounded ${canAdd
              ? 'bg-warning-100 text-warning-700 hover:bg-warning-200'
              : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'}`}>
            Add Late Fee
          </button>
        </div>
      </div>

      {/* Actionable fees: outstanding (Mark Paid / Waive) or pending (Approve / Reject) */}
      {actionable.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {actionable.map(f => (
            <div key={f.id} className="text-xs bg-neutral-50 rounded px-2 py-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-neutral-700 truncate">
                  {fmt(f.amount)}{f.note ? ` · ${f.note}` : ''} · {shortDate(f.applied_at)}
                  {f.state === 'pending' && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-800">Submitted</span>}
                </span>
              </div>

              {/* Pending: show submission detail + Approve / Reject */}
              {f.state === 'pending' && (
                <>
                  {(f.submission?.method || f.submission?.reference || f.submission?.note) && (
                    <p className="text-neutral-500 mt-1">
                      {[f.submission.method, f.submission.reference, f.submission.note].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {rejectingId === f.id ? (
                    <div className="mt-1.5 space-y-1.5">
                      <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={2}
                        placeholder="Reason for rejection…" autoFocus
                        className="w-full border border-neutral-300 rounded px-2 py-1 text-xs" />
                      <div className="flex gap-1">
                        <button onClick={() => rejectReason.trim() ? handleResolve(f.id, 'rejected', rejectReason.trim()) : setError('Rejection reason is required.')}
                          disabled={busy} className="px-2 py-0.5 bg-error-600 text-white rounded disabled:opacity-50">Confirm Rejection</button>
                        <button onClick={() => { setRejectingId(null); setRejectReason(''); setError(null); }}
                          className="px-2 py-0.5 border border-neutral-300 text-neutral-600 rounded">Back</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-1 mt-1.5">
                      <button onClick={() => handleResolve(f.id, 'paid')} disabled={busy}
                        className="px-2 py-0.5 bg-success-600 text-white rounded hover:bg-success-700 disabled:opacity-50">Approve Fee Payment</button>
                      <button onClick={() => { setError(null); setRejectingId(f.id); setRejectReason(''); }} disabled={busy}
                        className="px-2 py-0.5 bg-error-100 text-error-700 rounded hover:bg-error-200 disabled:opacity-50">Reject</button>
                    </div>
                  )}
                </>
              )}

              {/* Outstanding: Mark Paid (direct) / Waive */}
              {f.state === 'outstanding' && (
                <div className="flex items-center justify-between gap-2 mt-1.5">
                  {f.rejected && <span className="text-error-600 truncate">Rejected: {f.rejected.note || '—'}</span>}
                  <span className="flex gap-1 ml-auto flex-shrink-0">
                    <button onClick={() => handleResolve(f.id, 'paid')} disabled={busy}
                      className="px-2 py-0.5 border border-success-300 text-success-700 rounded hover:bg-success-50 disabled:opacity-50">Mark Paid</button>
                    <button onClick={() => handleResolve(f.id, 'waived')} disabled={busy}
                      className="px-2 py-0.5 border border-neutral-300 text-neutral-600 rounded hover:bg-neutral-50 disabled:opacity-50">Waive</button>
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Full event ledger */}
      {showHistory && (
        <div className="mt-2 space-y-1">
          {memberFees.length === 0 ? (
            <p className="text-xs text-neutral-400">No fees yet.</p>
          ) : memberFees.map(f => (
            <div key={f.id} className="text-xs text-neutral-500 bg-neutral-50 rounded px-2 py-1.5">
              <div className="text-neutral-600">{fmt(f.amount)} applied {shortDate(f.applied_at)}{f.note ? ` · ${f.note}` : ''}</div>
              {[...f.events].reverse().map(e => (
                <div key={e.id} className="pl-3">
                  ↳ {EVENT_LABEL[e.event] || e.event} ({e.actor_role}) {shortDate(e.created_at)}{e.note ? ` · ${e.note}` : ''}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {error && !showAdd && <p className="mt-1 text-xs text-error-600">{error}</p>}

      {/* Add Late Fee modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-4 z-[60]" onClick={() => setShowAdd(false)}>
          <div className="bg-neutral-100 rounded-lg w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-neutral-900 mb-3">Add Late Fee — {member.name}</h3>
            <label className="block text-sm text-neutral-700 mb-1">Amount</label>
            <div className="relative mb-3">
              <span className="absolute left-3 top-2 text-neutral-500">$</span>
              <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                className="w-full pl-7 pr-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500" autoFocus />
            </div>
            <label className="block text-sm text-neutral-700 mb-1">Note (optional)</label>
            <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. 2 days late"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm mb-3" />
            {error && <p className="text-xs text-error-600 mb-2">{error}</p>}
            <div className="flex gap-2">
              <button onClick={() => { setShowAdd(false); setError(null); }}
                className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-700">Cancel</button>
              <button onClick={handleAdd} disabled={busy}
                className="flex-1 px-3 py-2 bg-primary-600 text-neutral-50 rounded-lg text-sm font-semibold disabled:opacity-50">
                {busy ? 'Adding…' : 'Add Fee'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberLateFees;
